import { PropertyCondition } from '@/features/audienceEdit_v2/components/expressions/PropertyCondition';
import { ExpressionCondition } from '@/models/audiences';
import { FC, Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LuChevronDown, LuMapPin, LuPlus, LuRefreshCw, LuTrash2 } from 'react-icons/lu';
import { COMUNI_OPERATIONS } from '../constants';
import { useNewCommunicationActions } from './context/useNewCommunicationActions';
import { useNewCommunicationValues } from './context/useNewCommunicationValues';
import { FilterGroup } from './context/types';
import { GeoZoneChip, GeoZonePanel } from './geo/GeoZonePanel';

const JoinerPill: FC<{ isAnd: boolean; onAnd: () => void; onOr: () => void; andLabel: string; orLabel: string }> = ({
  isAnd,
  onAnd,
  onOr,
  andLabel,
  orLabel,
}) => (
  <div className='flex items-center gap-3.5 p-1'>
    <span className='inline-flex overflow-hidden rounded-lg border'>
      <button
        type='button'
        onClick={onAnd}
        className={`px-3.5 py-1.5 text-[11.5px] font-extrabold uppercase ${isAnd ? 'bg-brand-700 text-white-100' : 'bg-white-100 text-tertiary-600 hover:text-brand-700'}`}
      >
        {andLabel}
      </button>
      <button
        type='button'
        onClick={onOr}
        className={`px-3.5 py-1.5 text-[11.5px] font-extrabold uppercase ${!isAnd ? 'bg-brand-700 text-white-100' : 'bg-white-100 text-tertiary-600 hover:text-brand-700'}`}
      >
        {orLabel}
      </button>
    </span>
    <span className='grow border-t' />
  </div>
);

type RefineRuleBlockProps = {
  filterGroups: FilterGroup[];
  selectedDatasourceIds: string[];
  splitGroupAt: (groupKey: number, rowKey: number) => void;
  mergeGroupIntoPrevious: (groupKey: number) => void;
  updateRow: (groupKey: number, rowKey: number, condition: ExpressionCondition) => void;
  removeRow: (groupKey: number, rowKey: number) => void;
};

const RefineRuleBlock: FC<RefineRuleBlockProps> = ({ filterGroups, selectedDatasourceIds, splitGroupAt, mergeGroupIntoPrevious, updateRow, removeRow }) => {
  const { t } = useTranslation('comuni');

  return (
    <div className='w-full px-5 py-4'>
      {filterGroups.map((group, groupIndex) =>
        group.rows.map((row, rowIndex) => (
          <Fragment key={row.key}>
            {(groupIndex > 0 || rowIndex > 0) && (
              <JoinerPill
                isAnd={rowIndex === 0}
                andLabel={t('composer.andAlso')}
                orLabel={t('composer.orLabel')}
                onAnd={() => rowIndex > 0 && splitGroupAt(group.key, row.key)}
                onOr={() => rowIndex === 0 && mergeGroupIntoPrevious(group.key)}
              />
            )}
            <div className='flex items-center gap-3 rounded-xl border bg-white-100 p-4'>
              <div className='grow'>
                <PropertyCondition
                  expression={row.condition}
                  operationList={COMUNI_OPERATIONS}
                  datasourceIds={selectedDatasourceIds}
                  onChange={(condition) => updateRow(group.key, row.key, condition)}
                />
              </div>
              <button
                type='button'
                title={t('composer.removeFilter')}
                onClick={() => removeRow(group.key, row.key)}
                className='flex-none p-1.5 text-brand-700 hover:text-red-600'
              >
                <LuTrash2 className='size-[18px]' />
              </button>
            </div>
          </Fragment>
        ))
      )}
    </div>
  );
};

export const RefineRuleSection: FC = () => {
  const { t } = useTranslation('comuni');
  const { filterGroups, selectedDatasourceIds, activeFilterCount, selectedIds, isEstimating, zone } = useNewCommunicationValues();
  const { splitGroupAt, mergeGroupIntoPrevious, updateRow, removeRow, addRule, recalc, setZone } = useNewCommunicationActions();
  const [refineOpen, setRefineOpen] = useState(true);
  const [zoneOpen, setZoneOpen] = useState(false);
  const badgeCount = activeFilterCount + (zone ? 1 : 0);

  return (
    <div className='rounded-xl border bg-brand-50'>
      <button type='button' onClick={() => setRefineOpen((open) => !open)} className='flex w-full items-center gap-2.5 px-4 py-3 text-left'>
        <LuChevronDown className={`size-3.5 flex-none text-tertiary-600 ${refineOpen ? '' : '-rotate-90'}`} />
        <span className='text-sm font-extrabold text-secondary-700'>
          {t('composer.refine')} <span className='font-semibold text-quaternary-400'>({t('composer.optional')})</span>
        </span>
        {badgeCount > 0 && <span className='rounded-full bg-brand-50 px-2 py-0.5 text-[11px] font-extrabold text-brand-700'>{badgeCount}</span>}
      </button>
      {refineOpen && (
        <div className='border-t'>
          <RefineRuleBlock
            filterGroups={filterGroups}
            selectedDatasourceIds={selectedDatasourceIds}
            splitGroupAt={splitGroupAt}
            mergeGroupIntoPrevious={mergeGroupIntoPrevious}
            updateRow={updateRow}
            removeRow={removeRow}
          />
          {zone && !zoneOpen && <GeoZoneChip zone={zone} onOpen={() => setZoneOpen(true)} onRemove={() => setZone(null)} />}
          {zoneOpen && (
            <GeoZonePanel
              // the panel's own draft (ticked streets, drawn ring) belongs to these lists: remount on a change
              key={selectedDatasourceIds.join(',')}
              zone={zone}
              onClose={() => setZoneOpen(false)}
              onConfirm={(next) => {
                setZone(next);
                setZoneOpen(false);
              }}
            />
          )}
          <div className='flex items-center gap-3 px-5 pb-4'>
            <button
              type='button'
              onClick={addRule}
              className='flex items-center gap-2 rounded-full border-[1.5px] border-dashed border-brand-200 bg-white-100 px-4 py-2 text-[13px] font-bold text-brand-700 hover:border-brand-600'
            >
              <LuPlus className='size-3' strokeWidth={3} /> {t('composer.addFilter')}
            </button>
            <button
              type='button'
              onClick={() => setZoneOpen((open) => !open)}
              disabled={!selectedIds.length}
              className='flex items-center gap-2 rounded-full border-[1.5px] border-dashed border-brand-200 bg-white-100 px-4 py-2 text-[13px] font-bold text-brand-700 hover:border-brand-600 disabled:cursor-not-allowed disabled:border-quaternary-200 disabled:text-quaternary-300'
            >
              <LuMapPin className='size-3.5' /> {t('composer.geo.select')}
            </button>
            <span className='grow' />
            <button
              type='button'
              onClick={recalc}
              disabled={!selectedIds.length || isEstimating}
              className='flex items-center gap-2 rounded-[9px] border border-brand-200 bg-white-100 px-5 py-2 text-[13px] font-bold text-brand-700 hover:bg-brand-50 disabled:cursor-not-allowed disabled:opacity-50'
            >
              <LuRefreshCw className='size-3.5' /> {t('composer.recalc')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
